import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Bell, CalendarCheck, MessageSquareHeart, Pill, Sprout } from 'lucide-react';
import { HOME_COPY, SUBSCRIPTION_COPY } from '@saesak/shared';
import { AppBar, Screen } from '@/components/layout';
import { Card, ErrorNote, ReadOnlyBanner, SectionTitle, Spinner } from '@/components/ui';
import { apiData } from '@/lib/api';
import { formatRelative, greetingFor } from '@/lib/format';
import { useAuth } from '@/store/auth';
import type { StaffHome } from '@/types';

/** 교사·원장 홈 — "우리 반 오늘" (§7.5) */
export function StaffHomePage() {
  const membership = useAuth((s) => s.membership);
  const userName = useAuth((s) => s.user?.name ?? '');

  const { data, isPending, isError, refetch } = useQuery({
    queryKey: ['home', 'staff', membership?.facilityId],
    queryFn: () => apiData<StaffHome>('/home'),
  });

  const greeting = {
    morning: HOME_COPY.greetingMorning,
    afternoon: HOME_COPY.greetingAfternoon,
    evening: HOME_COPY.greetingEvening,
  }[greetingFor()];

  return (
    <Screen>
      <AppBar
        title={HOME_COPY.teacherTitle}
        subtitle={`${greeting}, ${userName} 선생님`}
        right={
          <Link
            to="/notifications"
            aria-label="알림"
            className="tap grid place-items-center rounded-full text-ink hover:bg-primary-soft"
          >
            <Bell size={20} />
          </Link>
        }
      />

      {membership && !membership.canWrite && (
        <ReadOnlyBanner message={SUBSCRIPTION_COPY.expiredBanner} />
      )}

      {isPending && <Spinner />}
      {isError && <ErrorNote message="오늘 현황을 불러오지 못했어요." onRetry={() => void refetch()} />}

      {data && (
        <div className="flex flex-col gap-4 p-4">
          <Card>
            <div className="mb-3 flex items-center justify-between">
              <p className="font-bold">오늘 출석</p>
              <Link to="/attendance" className="text-sm font-medium text-primary-strong underline">
                출석부 열기
              </Link>
            </div>
            <div className="grid grid-cols-3 gap-2">
              <Stat label="등원" value={data.attendance.present} tone="primary" />
              <Stat label="아직" value={data.attendance.notYet} tone="neutral" />
              <Stat label="결석" value={data.attendance.absent} tone="info" />
            </div>
          </Card>

          <div className="grid grid-cols-2 gap-3">
            <QuickCard
              to="/development-tasks"
              icon={<Sprout size={18} />}
              label="발달기록 할 일"
              value={data.developmentTasks}
            />
            <QuickCard
              to="/notes"
              icon={<CalendarCheck size={18} />}
              label="임시저장 알림장"
              value={data.draftNotes}
            />
          </div>

          {data.medications.length > 0 && (
            <section>
              <SectionTitle>오늘 투약</SectionTitle>
              <Card>
                <ul className="flex flex-col gap-1.5">
                  {data.medications.map((item) => (
                    <li key={item.id} className="flex items-center gap-2 text-base">
                      <Pill size={16} className="text-secondary" />
                      {item.child.name}
                    </li>
                  ))}
                </ul>
              </Card>
            </section>
          )}

          <section>
            <SectionTitle>
              가정에서 온 이야기
              <span className="ml-1 text-sm font-normal text-ink-sub">
                {data.homeMessages.length}건
              </span>
            </SectionTitle>
            {data.homeMessages.length === 0 ? (
              <Card>
                <p className="text-base text-ink-sub">{HOME_COPY.todoEmpty}</p>
              </Card>
            ) : (
              <ul className="flex flex-col gap-2">
                {data.homeMessages.map((message) => (
                  <Card key={message.id} as="li" className="flex gap-3">
                    <MessageSquareHeart size={18} className="mt-0.5 shrink-0 text-secondary" />
                    <div className="min-w-0">
                      <p className="text-base">{message.content}</p>
                      <p className="mt-0.5 text-sm text-ink-sub">
                        {formatRelative(message.createdAt)}
                      </p>
                    </div>
                  </Card>
                ))}
              </ul>
            )}
          </section>
        </div>
      )}
    </Screen>
  );
}

function Stat({
  label,
  value,
  tone,
}: {
  label: string;
  value: number;
  tone: 'primary' | 'neutral' | 'info';
}) {
  const bg = {
    primary: 'bg-primary-soft text-primary-strong',
    neutral: 'bg-bg text-ink-sub',
    info: 'bg-info-soft text-ink',
  }[tone];

  return (
    <div className={`rounded-sm px-3 py-3 text-center ${bg}`}>
      <p className="tnum text-2xl font-bold">{value}</p>
      <p className="text-sm">{label}</p>
    </div>
  );
}

function QuickCard({
  to,
  icon,
  label,
  value,
}: {
  to: string;
  icon: React.ReactNode;
  label: string;
  value: number;
}) {
  return (
    <Link to={to} className="card flex flex-col gap-1 p-4 transition-colors hover:bg-primary-soft">
      <span className="text-primary-strong">{icon}</span>
      <span className="tnum text-2xl font-bold">{value}</span>
      <span className="text-sm text-ink-sub">{label}</span>
    </Link>
  );
}
